import { EmailTemplateEditor } from '@/components/forms/EmailTemplateEditor';
import { Button } from '@/components/ui/Button';
import { domainsService } from '@/services/domains.service';
import { emailTemplatesService } from '@/services/emailTemplates.service';
import { useOrganisationsStore } from '@/store/organisations.store';
import {
  EmailTemplate,
  EmailTemplateFormData,
  SendBulletinsResponse,
} from '@/types/domain.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CheckCircle,
  ListChecks,
  Mail,
  Settings,
  X,
  XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BulletinsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  folderUuid: string;
  folderEmail?: string;
  onDataRefresh?: () => void;
}

interface BulletinsFormData {
  email: string;
  month: string;
  year: string;
}

type SendBulletinsResult = SendBulletinsResponse;

const buildLocalizedMonths = (locale: string) => {
  return Array.from({ length: 12 }, (_, idx) => {
    const monthNumber = idx + 1;
    const date = new Date(2000, idx, 1);
    const label = date.toLocaleString(locale || 'fr-FR', { month: 'long' });
    const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
    return { value: String(monthNumber), label: capitalized };
  });
};

const YEARS = Array.from({ length: 3 }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return { value: year.toString(), label: year.toString() };
});

export function BulletinsDrawer({
  isOpen,
  onClose,
  folderUuid,
  folderEmail,
  onDataRefresh,
}: BulletinsDrawerProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const orgUuid = useOrganisationsStore((s) => s.currentOrganisation?.uuid);
  const MONTHS = useMemo(
    () => buildLocalizedMonths(i18n.language),
    [i18n.language]
  );
  const [formData, setFormData] = useState<BulletinsFormData>({
    email: folderEmail || '',
    month: (new Date().getMonth() + 1).toString(),
    year: new Date().getFullYear().toString(),
  });
  const [result, setResult] = useState<SendBulletinsResult | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);
  const [selectedTemplateForSending, setSelectedTemplateForSending] =
    useState<string>('');

  // Fetch bulletins email templates from new service
  const { data: allTemplates } = useQuery({
    queryKey: ['email-templates-all', orgUuid],
    queryFn: () => emailTemplatesService.getAllTemplatesGrouped(),
    enabled: isOpen,
  });

  // Get bulletins templates specifically
  const bulletinsTemplates = allTemplates?.bulletins || [];


  const mutation = useMutation({
    mutationFn: (params: { month: number; year: number; email: string }) =>
      domainsService.sendBulletins(folderUuid, params),
    onSuccess: (response: SendBulletinsResult) => {
      setResult(response);
      setShowResult(true);
      // Refresh parent data if callback provided
      onDataRefresh?.();
    },
  });

  // Template management mutations
  const createTemplateMutation = useMutation({
    mutationFn: (templateData: EmailTemplateFormData) =>
      emailTemplatesService.createEmailTemplate({
        ...templateData,
        templateType: 'bulletins',
      }),
    onSuccess: (response) => {
      // Set the newly created template as selected for sending
      setSelectedTemplateForSending(response.data.uuid);
      setShowTemplateEditor(false);
      setSelectedTemplate(null);
      // Refresh templates list
      queryClient.invalidateQueries({ queryKey: ['email-templates-all'] });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({
      templateUuid,
      templateData,
    }: {
      templateUuid: string;
      templateData: EmailTemplateFormData;
    }) =>
      emailTemplatesService.updateEmailTemplate(templateUuid, templateData),
    onSuccess: () => {
      setShowTemplateEditor(false);
      setSelectedTemplate(null);
      // Refresh templates list
      queryClient.invalidateQueries({ queryKey: ['email-templates-all'] });
    },
  });

  const handleInputChange = (field: keyof BulletinsFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    if (!formData.email || !formData.month || !formData.year) return;
    mutation.mutate({
      email: formData.email,
      month: parseInt(formData.month),
      year: parseInt(formData.year),
    });
  };

  const handleClose = () => {
    setFormData({
      email: folderEmail || '',
      month: (new Date().getMonth() + 1).toString(),
      year: new Date().getFullYear().toString(),
    });
    setResult(null);
    setShowResult(false);
    setShowTemplateEditor(false);
    setSelectedTemplate(null);
    setSelectedTemplateForSending('');
    onClose();
  };

  const handleTemplateSave = async (templateData: EmailTemplateFormData) => {
    if (selectedTemplate) {
      await updateTemplateMutation.mutateAsync({
        templateUuid: selectedTemplate.uuid,
        templateData,
      });
    } else {
      await createTemplateMutation.mutateAsync(templateData);
    }
  };

  const handleOpenTemplateEditor = (template?: EmailTemplate) => {
    setSelectedTemplate(template || null);
    setShowTemplateEditor(true);
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-xl max-w-xl bg-white shadow-xl transition-transform">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-200 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">
                  {t('bulletins.title', 'Send payslips by email')}
                </h2>
                <p className="text-sm text-neutral-500">
                  {t(
                    'bulletins.description',
                    'Generate and email monthly payslips'
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {!showResult ? (
              <div className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-neutral-900">
                    {t('bulletins.email', 'Email address')}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    placeholder={t(
                      'bulletins.emailPlaceholder',
                      'email@example.com'
                    )}
                    className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                  />
                </div>

                {/* Date Selection */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-900">
                      {t('bulletins.period', 'Period')}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-neutral-700">
                        {t('bulletins.month', 'Month')}
                      </label>
                      <select
                        value={formData.month}
                        onChange={e =>
                          handleInputChange('month', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-neutral-700">
                        {t('bulletins.year', 'Year')}
                      </label>
                      <select
                        value={formData.year}
                        onChange={e =>
                          handleInputChange('year', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {YEARS.map(year => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Email Template Selection */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4 text-neutral-400" />
                      <span className="text-sm font-medium text-neutral-900">
                        {t('bulletins.emailTemplate', 'Template pour cet envoi')}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenTemplateEditor()}
                    >
                      {t('bulletins.createTemplate', 'Créer un template')}
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {/* Option par défaut */}
                    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50">
                      <input
                        type="radio"
                        name="template-selection"
                        value=""
                        checked={selectedTemplateForSending === ''}
                        onChange={() => setSelectedTemplateForSending('')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-neutral-900">
                          {t('bulletins.useDefaultTemplate', 'Utiliser le template par défaut')}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {t('bulletins.defaultTemplateDescription', 'Template système configuré pour ce type')}
                        </p>
                      </div>
                    </label>

                    {/* Templates disponibles */}
                    {bulletinsTemplates.map(template => (
                      <label
                        key={template.uuid}
                        className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 hover:bg-neutral-50"
                      >
                        <input
                          type="radio"
                          name="template-selection"
                          value={template.uuid}
                          checked={selectedTemplateForSending === template.uuid}
                          onChange={() => setSelectedTemplateForSending(template.uuid)}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-sm font-medium text-neutral-900">
                                {template.name}
                              </h5>
                              <p className="text-xs text-neutral-500">
                                {template.subject}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {template.isDefault && (
                                <span className="rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700">
                                  {t('common.default', 'Défaut')}
                                </span>
                              )}
                              {!template.isActive && (
                                <span className="rounded-full bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-600">
                                  {t('common.inactive', 'Inactif')}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleOpenTemplateEditor(template);
                                }}
                                className="text-neutral-400 hover:text-neutral-600"
                              >
                                <Settings className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}

                    {bulletinsTemplates.length === 0 && (
                      <div className="rounded-lg border border-dashed border-neutral-300 p-4 text-center">
                        <p className="text-sm text-neutral-500">
                          {t('bulletins.noTemplatesAvailable', 'Aucun template personnalisé disponible')}
                        </p>
                        <p className="text-xs text-neutral-400 mt-1">
                          {t('bulletins.createFirstTemplate', 'Créez votre premier template personnalisé ou utilisez le template par défaut')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Summary */}
                {formData.email && formData.month && formData.year && (
                  <div className="rounded-lg bg-neutral-50 p-4">
                    <h4 className="mb-2 text-sm font-medium text-neutral-900">
                      {t('bulletins.summary', 'Summary')}
                    </h4>
                    <div className="space-y-1 text-sm text-neutral-600">
                      <p>
                        <span className="font-medium">Email:</span>{' '}
                        {formData.email}
                      </p>
                      <p>
                        <span className="font-medium">Period:</span>{' '}
                        {MONTHS.find(m => m.value === formData.month)?.label}{' '}
                        {formData.year}
                      </p>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {mutation.isError && (
                  <div className="bg-error-50 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <XCircle className="text-error-600 h-4 w-4" />
                      <span className="text-error-900 text-sm font-medium">
                        {t('bulletins.error', 'Error while sending payslips')}
                      </span>
                    </div>
                    <p className="text-error-700 mt-1 text-sm">
                      {(mutation.error as unknown as { message?: string })
                        ?.message ||
                        t('common.genericError', 'An error occurred')}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Results
              <div className="space-y-6">
                <div className="rounded-lg bg-blue-50 p-4">
                  <div className="flex items-center gap-2">
                    {result?.success ? (
                      <CheckCircle className="text-success-600 h-4 w-4" />
                    ) : (
                      <XCircle className="text-error-600 h-4 w-4" />
                    )}
                    <span className="text-sm font-medium">
                      {result?.message}
                    </span>
                  </div>
                </div>

                <div className="rounded-lg border border-neutral-200 p-4">
                  <h4 className="mb-3 font-medium text-neutral-900">
                    {t('bulletins.details', 'Details')}
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-neutral-600">
                        {t('bulletins.generated', 'Payslips generated')}:
                      </span>
                      <p className="font-medium">
                        {result?.bulletsGenerated ?? 0}
                      </p>
                    </div>
                    <div>
                      <span className="text-neutral-600">
                        {t('bulletins.downloaded', 'PDFs downloaded')}:
                      </span>
                      <p className="font-medium">
                        {result?.pdfDownloaded ?? 0}
                      </p>
                    </div>
                    <div>
                      <span className="text-neutral-600">
                        {t('bulletins.emailSent', 'Email sent')}:
                      </span>
                      <p className="font-medium">
                        {result?.emailSent
                          ? t('common.yes', 'Yes')
                          : t('common.no', 'No')}
                      </p>
                    </div>
                  </div>
                </div>

                {result?.employeeNames?.length ? (
                  <div className="rounded-lg border border-neutral-200 p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <ListChecks className="h-4 w-4 text-neutral-500" />
                      <h4 className="font-medium text-neutral-900">
                        {t('bulletins.employees', 'Employees')}
                      </h4>
                    </div>
                    <ul className="list-disc pl-5 text-sm text-neutral-700">
                      {result.employeeNames.map((name, idx) => (
                        <li key={idx}>{name}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-neutral-200 p-6">
            {!showResult ? (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={
                    !formData.email ||
                    !formData.month ||
                    !formData.year ||
                    mutation.isPending
                  }
                  className="flex-1"
                >
                  {mutation.isPending
                    ? t('common.loading', 'Loading...')
                    : t('bulletins.send', 'Send')}
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowResult(false);
                    setResult(null);
                  }}
                  className="flex-1"
                >
                  {t('common.back', 'Back')}
                </Button>
                <Button onClick={handleClose} className="flex-1">
                  {t('common.close', 'Close')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Email Template Editor */}
      <EmailTemplateEditor
        template={selectedTemplate}
        templateType="bulletins"
        folderUuid="" // Not used anymore with new architecture
        isOpen={showTemplateEditor}
        onClose={() => {
          setShowTemplateEditor(false);
          setSelectedTemplate(null);
        }}
        onSave={handleTemplateSave}
        defaultTemplate={null}
      />
    </div>
  );
}
